import { Injectable } from '@angular/core';
import { AuthService } from '@App/app/auth/auth.service';
import { GigaPixelsPipe } from '@App/app/auth/giga-pixels.pipe';
import { Credits } from '@App/app/entities/admin-panel/credits.model';
import { ProductionForm } from '@App/app/entities/forms/production-form.model';
import { BuildProcessTypeId } from '@App/app/entities/processing/build-process-status.model';
import { BuildProcessStep } from '@App/app/entities/processing/build-process-step.model';
import { BuildProcess } from '@App/app/entities/processing/build-process.model';
import { ModelsProcessingService } from '@App/app/pages/processing/services/models-processing-service/models-processing.service';
import { ReportProblemDialogComponent } from '@App/app/shared/components/report-problem-dialog/report-problem-dialog/report-problem-dialog.component';
import { selectAllUserCompanies } from '@App/app/shared/store/user-companies/selectors/user-companies.selectors';
import { NbDialogService } from '@nebular/theme';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { combineLatest } from 'rxjs';
import { filter, take } from 'rxjs/operators';

@UntilDestroy({ checkProperties: true })
@Injectable()
export class ProcessingSidebarCreditsService {
  private credits: Credits | null;
  private currentBuildProcess: BuildProcess;
  notEnoughCreditsMessage = '';
  missingCredits: string;

  constructor(
    private authService: AuthService,
    private modelsProcessingService: ModelsProcessingService,
    private store: Store,
    private dialogService: NbDialogService,
    private gigaPixelPipe: GigaPixelsPipe,
  ) {
    this.modelsProcessingService.currentBuildProcess
      .pipe(untilDestroyed(this), filter<BuildProcess>(Boolean), take(1))
      .subscribe((process) => {
        this.currentBuildProcess = process;
      });

    // TODO: create reusable selector for credits - it might be located in credits service
    combineLatest([this.authService.currentEndpoint$, this.store.select(selectAllUserCompanies)])
      .pipe(untilDestroyed(this))
      .subscribe(([endpoint, companies]) => {
        if (endpoint && companies) {
          this.credits = companies.find((company) => company.id === endpoint.id)?.credits || null;
        }
      });
  }

  checkIfEnoughCreditsToProcess(stepTypeToProcess: BuildProcessTypeId, form: any): boolean {
    this.notEnoughCreditsMessage = '';

    if (!this.credits?.enabled) {
      return true;
    }

    if (this.aboutToProcessFirstTime(stepTypeToProcess)) {
      return this.checkIfEnoughCreditsForFirstProcessing(stepTypeToProcess, form);
    }

    return this.checkIfEnoughCreditsForReprocessing(stepTypeToProcess, form);
  }

  getCreditsAlert(stepTypeToProcess: BuildProcessTypeId, form: any) {
    const alerts: {
      text: string;
      status: 'warning' | 'info';
      alignment: 'left' | 'center' | 'right';
    }[] = [];

    if (!this.credits?.enabled) {
      return alerts;
    }

    if (!this.aboutToProcessFirstTime(stepTypeToProcess)) {
      alerts.push({
        text: `Credits (${this.gigaPixelPipe.transform(
          this.currentBuildProcess.declaredPixelsAmount,
        )} pixels) will be charged for reprocessing.`,
        alignment: 'left' as 'left' | 'center' | 'right',
        status: 'warning' as 'warning' | 'info',
      });
    }

    if (stepTypeToProcess === BuildProcessTypeId.Prod) {
      const selectedFormatsAmount = this.getSelectedFormatsAmount(form as ProductionForm['value']);
      if (selectedFormatsAmount > 1) {
        alerts.push({
          text: `Extra credits (${this.gigaPixelPipe.transform(
            this.currentBuildProcess.declaredPixelsAmount * (selectedFormatsAmount - 1),
          )} pixels) will be charged for additional formats.`,
          alignment: 'left' as 'left' | 'center' | 'right',
          status: 'warning' as 'warning' | 'info',
        });
      }
    }

    return alerts;
  }

  showProblemReport() {
    this.dialogService.open(ReportProblemDialogComponent, {
      context: {
        category: 'processing',
        subcategory: 'credits',
        title: 'Processing Credit Request',
        description: `I would like to add ${this.missingCredits} credits to my credit pool.`,
      },
    });
  }

  private checkIfEnoughCreditsForFirstProcessing(stepTypeToProcess: BuildProcessTypeId, form: any) {
    this.notEnoughCreditsMessage = '';

    if (stepTypeToProcess !== BuildProcessTypeId.Prod) {
      return true; // Credits already charged during upload step.
    }

    const selectedFormatsAmount = this.getSelectedFormatsAmount(form as ProductionForm['value']);
    if (selectedFormatsAmount === 1) {
      return true; // Credits already charged during upload step.
    }

    if (this.isEnoughCreditsForFormats(selectedFormatsAmount - 1)) {
      return true;
    }

    this.missingCredits = this.getMissingGigaPixelsCreditsForFormats(selectedFormatsAmount - 1);
    this.notEnoughCreditsMessage = `Not enough credits to process additional formats - need <strong> ${this.missingCredits}</strong> more credits. Select less formats or`;
    return false;
  }

  private checkIfEnoughCreditsForReprocessing(stepTypeToProcess: BuildProcessTypeId, form: any) {
    this.notEnoughCreditsMessage = '';

    if (stepTypeToProcess !== BuildProcessTypeId.Prod) {
      return this.checkIfEnoughCreditsToReprocessBaseFormat();
    }

    const selectedFormatsAmount = this.getSelectedFormatsAmount(form as ProductionForm['value']);
    if (selectedFormatsAmount === 1) {
      return this.checkIfEnoughCreditsToReprocessBaseFormat();
    }

    if (this.isEnoughCreditsForFormats(selectedFormatsAmount)) {
      return true;
    }

    this.missingCredits = this.getMissingGigaPixelsCreditsForFormats(selectedFormatsAmount);
    this.notEnoughCreditsMessage = `Not enough credits to reprocess selected formats - need <strong>${this.missingCredits}</strong> more credits. Try to select less formats or`;
    return false;
  }

  private checkIfEnoughCreditsToReprocessBaseFormat() {
    this.notEnoughCreditsMessage = '';

    if (this.isEnoughCreditsForFormats(1)) {
      return true;
    }

    this.missingCredits = this.getMissingGigaPixelsCreditsForFormats(1);
    this.notEnoughCreditsMessage = `Not enough credits to reprocess - need <strong>${this.missingCredits}</strong> more credits.`;
    return false;
  }

  private isEnoughCreditsForFormats(formatsAmount: number) {
    return (
      this.currentBuildProcess.declaredPixelsAmount * formatsAmount <= (this.credits?.value || 0)
    );
  }

  private getMissingGigaPixelsCreditsForFormats(formats: number) {
    return this.gigaPixelPipe.transform(
      this.currentBuildProcess.declaredPixelsAmount * formats - (this.credits?.value || 0),
    );
  }

  private getSelectedFormatsAmount(form: ProductionForm['value']) {
    const { withObj, withPointCloud, withOrthoDSM } = form;
    return [withObj, withPointCloud, withOrthoDSM].filter(Boolean).length;
  }

  private aboutToProcessFirstTime(stepTypeToProcess: BuildProcessTypeId) {
    const currentStep = this.currentBuildProcess.getCurrentStep() as BuildProcessStep;
    return stepTypeToProcess > currentStep.type;
  }
}
